/* eslint-disable no-nested-ternary */
import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, MenuItem, Select } from '@mui/material';
import { AwardTemplate } from 'components/customers/models/Award';
import { CustomerSegment } from 'components/customers/models/Customer';
import { Perk } from 'components/loyalty/models/PerkModel';
import { Option } from 'lib/types';
import { toggleCreateAudienceModal, toggleCreatePerkModal } from 'store/campaigns/campaignsAction';
import { CampaignPaper } from '../CampaignPaper';
import { CampaignEffectData } from '../models/CampaignItem';
import { CampaignEffect, campaignTriggerEffectLabel } from '../models/CampaignTriggerEffect';
import { AddAudienceEffect } from './effects/AddAudienceEffect';
import { PerkEffect } from './effects/PerkEffect';

interface CampaignThenBlockProps {
    perks: Perk[];
    audiences: CustomerSegment[];
    onSubmit: (data: CampaignEffectData) => void;
    effect?: CampaignEffectData;
    index: number;
}

const effects = Object.entries(campaignTriggerEffectLabel).map(([value, label]) => ({ value, label }));

const isSegmentEffect = (effect: string) => effect === CampaignEffect.ADD_SEGMENT_USER;
const findPerk = (perks: Perk[], perkId: string) => perks.find(item => item._id === perkId);

export const CampaignThenBlock: React.FC<CampaignThenBlockProps> = ({
    audiences,
    perks,
    onSubmit,
    effect,
    index
}) => {
    // state
    const { type: effectType, target: effectTarget = null, points = '' } = effect || {};
    const dispatch = useDispatch();

    // calculated
    const segmentEffect = React.useMemo(() => isSegmentEffect(effectType), [effectType]);
    const currentSegment = React.useMemo(
        () => (segmentEffect ? audiences.find(item => item._id === effectTarget) : null),
        [audiences, segmentEffect, effectTarget]
    );
    const currentPerk = React.useMemo(
        () => (segmentEffect ? null : findPerk(perks, effectTarget)),
        [segmentEffect, effectTarget, perks]
    );
    const stampPerk = React.useMemo(
        () =>
            currentPerk &&
            effectType !== CampaignEffect.REMOVE_AWARD &&
            currentPerk.template === AwardTemplate.STAMP_CARD,
        [currentPerk, effectType]
    );
    const isChecked = segmentEffect ? !!effectTarget : stampPerk ? !!points : !!currentPerk;

    // handlers
    const handleTypeChange = React.useCallback(
        (e: StrictSelectEvent<CampaignEffect>) => {
            if (effectType !== e.target.value) {
                onSubmit({ type: e.target.value, target: null, points: '' });
            } else {
                onSubmit({ ...effect, type: e.target.value });
            }
        },
        [effectType, effect, onSubmit]
    );
    const handleTargetChange = React.useCallback(
        (value: Option) => {
            onSubmit({ type: effectType, target: value.value });
        },
        [effectType, onSubmit]
    );
    const handlePointsChange = React.useCallback(
        (e: React.ChangeEvent<{ value: string }>) => {
            onSubmit({ type: effectType, target: effectTarget, points: e.target.value });
        },
        [effectTarget, effectType, onSubmit]
    );
    const handleCreate = React.useCallback(
        (title: string) => {
            if (segmentEffect) {
                toggleCreateAudienceModal(index, title)(dispatch);
            } else {
                toggleCreatePerkModal(index, title)(dispatch);
            }
        },
        [segmentEffect, dispatch, index]
    );

    return (
        <CampaignPaper isChecked={isChecked} title="Then...">
            <Grid container spacing={2} alignItems="flex-end">
                <Grid item>
                    <Box minWidth={200}>
                        <Select
                            fullWidth
                            value={effectType || ''}
                            onChange={handleTypeChange}
                            placeholder="Choose an effect"
                        >
                            {effects.map(({ value, label }) => (
                                <MenuItem key={`select-condition-type-${value}`} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>
                {!!effectType && segmentEffect && (
                    <AddAudienceEffect
                        value={
                            currentSegment ? { value: currentSegment._id, label: currentSegment.title } : null
                        }
                        onChange={handleTargetChange}
                        onCreate={handleCreate}
                        audiences={audiences}
                    />
                )}
                {!!effectType && !segmentEffect && (
                    <PerkEffect
                        value={currentPerk ? { value: currentPerk._id, label: currentPerk.title } : null}
                        onChange={handleTargetChange}
                        perks={perks}
                        onCreate={handleCreate}
                        points={points}
                        onPointsChange={handlePointsChange}
                    />
                )}
            </Grid>
        </CampaignPaper>
    );
};
